import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Schema for validation
const whatsAppServerSchema = z.object({
  id: z.string().uuid(),
  nome: z.string(),
  url: z.string(),
  api_token: z.string(),
  max_sessions: z.number().nullable(),
  current_sessions: z.number().nullable(),
  is_shared: z.boolean().nullable(),
  status: z.string().nullable(),
  last_heartbeat: z.string().nullable(),
  region: z.string().nullable(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
});

export type WhatsAppServer = z.infer<typeof whatsAppServerSchema>;

export interface ServerHealth {
  service: string;
  sessions: number;
  maxSessions: number;
  available: boolean;
  error?: string;
}

export interface AllServersHealth {
  servers: Array<{
    id: string;
    name: string;
    url: string;
    health: ServerHealth | null;
    error?: string;
  }>;
  totalAvailable: number;
  totalSessions: number;
  totalMaxSessions: number;
}

// Fetch all servers
async function fetchServers(): Promise<WhatsAppServer[]> {
  const { data, error } = await supabase
    .from('wpp_services')
    .select('*')
    .order('nome');

  if (error) throw error;

  const parsed = z.array(whatsAppServerSchema).safeParse(data);
  if (!parsed.success) {
    console.error('Invalid wpp_services data:', parsed.error.flatten());
    throw new Error('Invalid WhatsApp servers data');
  }

  return parsed.data;
}

// Fetch available server for individual sessions (with slots)
async function fetchAvailableServer(): Promise<WhatsAppServer | null> {
  const { data, error } = await supabase
    .from('wpp_services')
    .select('*')
    .eq('is_shared', false)
    .eq('status', 'active')
    .order('current_sessions', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const maxSessions = data.max_sessions ?? 3;
  const currentSessions = data.current_sessions ?? 0;
  
  if (currentSessions >= maxSessions) {
    return null;
  }

  const parsed = whatsAppServerSchema.safeParse(data);
  if (!parsed.success) return null;

  return parsed.data;
}

// Fetch shared server
async function fetchSharedServer(): Promise<WhatsAppServer | null> {
  const { data, error } = await supabase
    .from('wpp_services')
    .select('*')
    .eq('is_shared', true)
    .eq('status', 'active')
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const parsed = whatsAppServerSchema.safeParse(data);
  if (!parsed.success) return null;

  return parsed.data;
}

// Fetch server by ID
export async function fetchServerById(serverId: string): Promise<WhatsAppServer | null> {
  const { data, error } = await supabase
    .from('wpp_services')
    .select('*')
    .eq('id', serverId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const parsed = whatsAppServerSchema.safeParse(data);
  if (!parsed.success) return null;

  return parsed.data;
}

// Check health of a single server (direct HTTP call to Railway)
export async function checkServerHealth(server: WhatsAppServer): Promise<ServerHealth | null> {
  try {
    const response = await fetch(`${server.url}/health`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${server.api_token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Health check failed: ${response.status}`);
    }

    const data = await response.json();
    return {
      service: data.service || server.nome,
      sessions: data.sessions ?? 0,
      maxSessions: data.maxSessions ?? server.max_sessions ?? 3,
      available: data.available ?? true,
    };
  } catch (error) {
    console.error(`Health check failed for ${server.nome}:`, error);
    return null;
  }
}

// Check health of all servers
export async function checkAllServersHealth(servers: WhatsAppServer[]): Promise<AllServersHealth> {
  const results = await Promise.all(
    servers.map(async (server) => {
      const health = await checkServerHealth(server);
      return {
        id: server.id,
        name: server.nome,
        url: server.url,
        health,
        error: health ? undefined : 'Failed to connect',
      };
    })
  );

  const healthyServers = results.filter(r => r.health !== null);
  
  return {
    servers: results,
    totalAvailable: healthyServers.filter(r => r.health?.available).length,
    totalSessions: healthyServers.reduce((acc, r) => acc + (r.health?.sessions ?? 0), 0),
    totalMaxSessions: healthyServers.reduce((acc, r) => acc + (r.health?.maxSessions ?? 0), 0),
  };
}

// Allocate best available server for a new session
export async function allocateServer(): Promise<WhatsAppServer | null> {
  const { data: servers, error } = await supabase
    .from('wpp_services')
    .select('*')
    .eq('is_shared', false)
    .eq('status', 'active')
    .order('current_sessions', { ascending: true });

  if (error || !servers?.length) return null;

  // Find first server with available slots
  for (const server of servers) {
    const maxSessions = server.max_sessions ?? 3;
    const currentSessions = server.current_sessions ?? 0;
    
    if (currentSessions < maxSessions) {
      const parsed = whatsAppServerSchema.safeParse(server);
      if (parsed.success) return parsed.data;
    }
  }

  return null;
}

export function useWhatsAppServers() {
  const queryClient = useQueryClient();

  // Query all servers
  const serversQuery = useQuery({
    queryKey: ['whatsAppServers'],
    queryFn: fetchServers,
    staleTime: 1000 * 30,
  });

  // Query available server (individual)
  const availableServerQuery = useQuery({
    queryKey: ['whatsAppServers', 'available'],
    queryFn: fetchAvailableServer,
    staleTime: 1000 * 10,
  });

  // Query shared server
  const sharedServerQuery = useQuery({
    queryKey: ['whatsAppServers', 'shared'],
    queryFn: fetchSharedServer,
    staleTime: 1000 * 30,
  });

  // Query all servers health
  const allHealthQuery = useQuery({
    queryKey: ['whatsAppServers', 'health'],
    queryFn: async () => {
      const servers = await fetchServers();
      return checkAllServersHealth(servers);
    },
    staleTime: 1000 * 15,
    enabled: false, // Manual trigger only
  });

  // Update server mutation
  const updateServerMutation = useMutation({
    mutationFn: async ({ 
      id, 
      updates 
    }: { 
      id: string; 
      updates: Partial<Pick<WhatsAppServer, 'url' | 'api_token' | 'max_sessions' | 'status' | 'region'>> 
    }) => {
      const { data, error } = await supabase
        .from('wpp_services')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsAppServers'] });
      toast.success('Server updated successfully!');
    },
    onError: (error) => {
      toast.error(`Error updating server: ${error.message}`);
    },
  });

  // Create server mutation
  const createServerMutation = useMutation({
    mutationFn: async (newServer: {
      nome: string;
      url: string;
      api_token: string;
      max_sessions?: number;
      is_shared?: boolean;
      region?: string;
    }) => {
      const { data, error } = await supabase
        .from('wpp_services')
        .insert([newServer])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsAppServers'] });
      toast.success('Server created successfully!');
    },
    onError: (error) => {
      toast.error(`Error creating server: ${error.message}`);
    },
  });

  // Delete server mutation
  const deleteServerMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('wpp_services')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsAppServers'] });
      toast.success('Server removed successfully!');
    },
    onError: (error) => {
      toast.error(`Error removing server: ${error.message}`);
    },
  });

  // Increment sessions mutation
  const incrementSessionsMutation = useMutation({
    mutationFn: async (serverId: string) => {
      const { data: server } = await supabase
        .from('wpp_services')
        .select('current_sessions')
        .eq('id', serverId)
        .single();

      if (server) {
        const { data, error } = await supabase
          .from('wpp_services')
          .update({ current_sessions: (server.current_sessions ?? 0) + 1 })
          .eq('id', serverId)
          .select()
          .single();

        if (error) throw error;
        return data;
      }
      return null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsAppServers'] });
    },
  });

  // Decrement sessions mutation
  const decrementSessionsMutation = useMutation({
    mutationFn: async (serverId: string) => {
      const { data: server } = await supabase
        .from('wpp_services')
        .select('current_sessions')
        .eq('id', serverId)
        .single();

      if (server) {
        const newCount = Math.max(0, (server.current_sessions ?? 0) - 1);
        await supabase
          .from('wpp_services')
          .update({ current_sessions: newCount })
          .eq('id', serverId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsAppServers'] });
    },
  });

  // Sync session counts from Railway health endpoints
  const syncSessionCountsMutation = useMutation({
    mutationFn: async () => {
      const servers = await fetchServers();
      const updates: Promise<void>[] = [];

      for (const server of servers) {
        const health = await checkServerHealth(server);
        if (health) {
          await supabase
            .from('wpp_services')
            .update({ 
              current_sessions: health.sessions,
              last_heartbeat: new Date().toISOString(),
            })
            .eq('id', server.id);
        }
      }

      await Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsAppServers'] });
      toast.success('Session counts synced!');
    },
    onError: (error) => {
      toast.error(`Error syncing sessions: ${error.message}`);
    },
  });

  return {
    // Queries
    servers: serversQuery.data ?? [],
    serversLoading: serversQuery.isLoading,
    serversError: serversQuery.error,
    
    availableServer: availableServerQuery.data ?? null,
    availableServerLoading: availableServerQuery.isLoading,
    
    sharedServer: sharedServerQuery.data ?? null,
    sharedServerLoading: sharedServerQuery.isLoading,

    allHealth: allHealthQuery.data ?? null,
    allHealthLoading: allHealthQuery.isLoading,
    checkAllHealth: allHealthQuery.refetch,

    // Mutations
    updateServer: updateServerMutation.mutate,
    updateServerAsync: updateServerMutation.mutateAsync,
    isUpdating: updateServerMutation.isPending,

    createServer: createServerMutation.mutate,
    createServerAsync: createServerMutation.mutateAsync,
    isCreating: createServerMutation.isPending,

    deleteServer: deleteServerMutation.mutate,
    deleteServerAsync: deleteServerMutation.mutateAsync,
    isDeleting: deleteServerMutation.isPending,

    incrementSessions: incrementSessionsMutation.mutate,
    decrementSessions: decrementSessionsMutation.mutate,

    syncSessionCounts: syncSessionCountsMutation.mutate,
    isSyncing: syncSessionCountsMutation.isPending,

    // Helpers
    fetchServerById,
    allocateServer,
    refetch: () => {
      serversQuery.refetch();
      availableServerQuery.refetch();
      sharedServerQuery.refetch();
    },
  };
}
